'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useLocale } from 'next-intl'
import { usePathname } from 'next/navigation'
import { routing } from '@/i18n/routing'

const LABELS: Record<string, string> = { en: 'English', no: 'Norsk' }

// Text links to every locale. Server-rendered href is the locale's home
// (crawlable, always valid); after hydration each link upgrades to THIS page's
// sibling in that locale by reading the page's own hreflang alternate links —
// the same URLs search engines get, so the two can never disagree. Pages
// without a translation keep the home fallback. Literal home hrefs because
// next-intl's Link emits /en for the default locale under 'as-needed'.
function localeHome(locale: string): string {
  return locale === routing.defaultLocale ? '/' : `/${locale}`
}

function useAlternateHrefs(): Record<string, string> {
  const pathname = usePathname()
  const [hrefs, setHrefs] = useState<Record<string, string>>({})

  useEffect(() => {
    const found: Record<string, string> = {}
    for (const locale of routing.locales) {
      const link = document.querySelector<HTMLLinkElement>(
        `link[rel="alternate"][hreflang="${locale}"]`
      )
      if (link?.href) {
        try {
          found[locale] = new URL(link.href).pathname
        } catch {
          /* malformed href — keep home fallback */
        }
      }
    }
    setHrefs(found)
  }, [pathname])

  return hrefs
}

export function LanguageSwitcher() {
  const active = useLocale()
  const alternates = useAlternateHrefs()

  return (
    <nav aria-label="Language" className="flex gap-x-[24px] text-l text-[#666666]">
      {routing.locales.map((locale) => (
        <Link
          key={locale}
          href={alternates[locale] ?? localeHome(locale)}
          hrefLang={locale}
          aria-current={locale === active ? 'true' : undefined}
          className={locale === active ? 'font-bold' : ''}
        >
          {LABELS[locale]}
        </Link>
      ))}
    </nav>
  )
}
